<template>
  <div class="reg-detail-page w-100">
    <!-- Header -->
    <div class="w-100 px-8 py-4 bg-white border-bottom">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <button class="btn btn-icon btn-light mr-3" @click="goBack">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div>
            <h5 class="mb-0 font-weight-bold text-dark">Chi tiết Đăng ký</h5>
            <small class="text-muted">Thông tin chi tiết và lịch sử sử dụng</small>
          </div>
        </div>
        <button
          class="btn font-weight-medium"
          :class="detail && !isActive ? 'btn-danger' : 'btn-success'"
          @click="goToUsageHistory"
          :disabled="!detail"
          :title="detail && !isActive ? `Gói hết hạn vào ${formatDate(detail.endDate)}` : 'Ghi nhận sử dụng'"
        >
          <i class="fas fa-clipboard-list mr-2"></i>Ghi nhận sử dụng
          <span v-if="detail && !isActive" class="ml-1 badge badge-light text-danger" style="font-size:0.72rem;">Hết hạn</span>
        </button>
      </div>
    </div>

    <!-- Content -->
    <b-overlay :show="loading">
      <div class="container-fluid py-6">
        <div class="row justify-content-center">
          <div class="col-lg-10" v-if="detail">

            <!-- Top info row: Customer + Registration -->
            <div class="row mb-5">
              <!-- Customer info -->
              <div class="col-md-6 mb-4">
                <div class="info-card h-100">
                  <div class="info-card__header">
                    <div class="info-card__icon info-card__icon--blue">
                      <i class="fas fa-user"></i>
                    </div>
                    <h6 class="info-card__title">Thông tin khách hàng</h6>
                    <button
                      class="btn btn-icon btn-sm btn-light-primary ml-auto"
                      title="Sửa thông tin khách hàng"
                      @click="openEditCustomer"
                    >
                      <i class="fas fa-pen"></i>
                    </button>
                  </div>
                  <div class="info-card__body">
                    <div class="info-row">
                      <span class="info-row__label">Họ tên:</span>
                      <span class="info-row__value font-weight-bold">{{ customerName }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-row__label">Email:</span>
                      <span class="info-row__value">{{ customerEmail }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-row__label">Số điện thoại:</span>
                      <span class="info-row__value">{{ customerPhone }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-row__label">Ngày sinh:</span>
                      <span class="info-row__value">{{ customerDob }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-row__label">Giới tính:</span>
                      <span class="info-row__value">{{ customerGender }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Registration info -->
              <div class="col-md-6 mb-4">
                <div class="info-card h-100">
                  <div class="info-card__header">
                    <div class="info-card__icon info-card__icon--green">
                      <i class="fas fa-clipboard-check"></i>
                    </div>
                    <h6 class="info-card__title">Thông tin đăng ký</h6>
                    <span :class="statusBadgeClass" class="status-pill ml-auto">
                      {{ statusLabel }}
                    </span>
                  </div>
                  <div class="info-card__body">
                    <div class="info-row">
                      <span class="info-row__label">Gói dịch vụ:</span>
                      <span class="info-row__value font-weight-bold text-primary">{{ packageName }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-row__label">Ngày đăng ký:</span>
                      <span class="info-row__value">{{ formatDate(detail.startDate) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-row__label">Ngày hết hạn:</span>
                      <span class="info-row__value">{{ formatDate(detail.endDate) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-row__label">Tổng thanh toán:</span>
                      <span class="info-row__value font-weight-bold text-success">{{ formatPrice(detail.priceBuy) }} VNĐ</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Items usage row -->
            <div class="row mb-5">
              <!-- Medicines usage -->
              <div class="col-md-4 mb-4" v-if="detail.tranMedicine && detail.tranMedicine.length > 0">
                <div class="remaining-card h-100">
                  <h6 class="remaining-card__title">
                    <i class="fas fa-pills mr-2 text-info"></i>Thuốc đã sử dụng
                  </h6>
                  <div class="remaining-card__body">
                    <div
                      v-for="med in detail.tranMedicine"
                      :key="med.id"
                      class="remaining-item"
                    >
                      <div class="d-flex justify-content-between mb-1">
                        <span class="remaining-item__name">{{ med.shortName || med.name }}</span>
                        <span class="remaining-item__count">
                          {{ med.isUnlimited ? (usageByItemId[med.id] || 0) + '/∞' : (usageByItemId[med.id] || 0) + '/' + med.tranMedicineCount }}
                        </span>
                      </div>
                      <b-progress
                        v-if="!med.isUnlimited"
                        :value="usageByItemId[med.id] || 0"
                        :max="med.tranMedicineCount"
                        :variant="getUsageVariant(usageByItemId[med.id], med.tranMedicineCount)"
                        height="6px"
                        class="remaining-item__bar"
                      ></b-progress>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Service Packages usage -->
              <div class="col-md-4 mb-4" v-if="detail.tranServicePackage && detail.tranServicePackage.length > 0">
                <div class="remaining-card h-100">
                  <h6 class="remaining-card__title">
                    <i class="fas fa-box mr-2 text-success"></i>Service Package
                  </h6>
                  <div class="remaining-card__body">
                    <div
                      v-for="pkg in detail.tranServicePackage"
                      :key="pkg.id"
                      class="remaining-item"
                    >
                      <div class="d-flex justify-content-between mb-1">
                        <span class="remaining-item__name">{{ pkg.name }}</span>
                        <span class="remaining-item__count">
                          {{ pkg.isUnlimited ? (usageByItemId[pkg.id] || 0) + '/∞' : (usageByItemId[pkg.id] || 0) + '/' + pkg.tranServicePackageCount }}
                        </span>
                      </div>
                      <b-progress
                        v-if="!pkg.isUnlimited"
                        :value="usageByItemId[pkg.id] || 0"
                        :max="pkg.tranServicePackageCount"
                        :variant="getUsageVariant(usageByItemId[pkg.id], pkg.tranServicePackageCount)"
                        height="6px"
                        class="remaining-item__bar"
                      ></b-progress>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Services usage -->
              <div class="col-md-4 mb-4" v-if="detail.tranService && detail.tranService.length > 0">
                <div class="remaining-card h-100">
                  <h6 class="remaining-card__title">
                    <i class="fas fa-concierge-bell mr-2 text-purple"></i>Dịch vụ đã sử dụng
                  </h6>
                  <div class="remaining-card__body">
                    <div
                      v-for="svc in detail.tranService"
                      :key="svc.id"
                      class="remaining-item"
                    >
                      <div class="d-flex justify-content-between mb-1">
                        <span class="remaining-item__name">{{ svc.name }}</span>
                        <span class="remaining-item__count">
                          {{ svc.isUnlimited ? (usageByItemId[svc.id] || 0) + '/∞' : (usageByItemId[svc.id] || 0) + '/' + svc.tranServiceCount }}
                        </span>
                      </div>
                      <b-progress
                        v-if="!svc.isUnlimited"
                        :value="usageByItemId[svc.id] || 0"
                        :max="svc.tranServiceCount"
                        :variant="getUsageVariant(usageByItemId[svc.id], svc.tranServiceCount)"
                        height="6px"
                        class="remaining-item__bar"
                      ></b-progress>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Usage History -->
            <div class="usage-history-card" v-if="detail.serviceUsage && detail.serviceUsage.length > 0">
              <h6 class="usage-history-card__title">
                <i class="fas fa-history mr-2 text-muted"></i>Lịch sử sử dụng gần đây
              </h6>
              <div class="usage-history-card__body">
                <div
                  v-for="usage in detail.serviceUsage"
                  :key="usage.id"
                  class="usage-row"
                >
                  <div class="usage-row__info">
                    <div class="usage-row__name font-weight-bold">
                      {{ getUsageItemName(usage) }}
                    </div>
                    <div class="usage-row__meta text-muted">
                      {{ getItemTypeLabel(usage.itemType) }} • Số lượng: {{ usage.quantity }}
                    </div>
                     <div class="usage-row__name font-weight-bold">
                      {{ usage.note }}
                    </div>
                  </div>
                  <div class="usage-row__date text-muted">
                    {{ formatDate(usage.createDatetime) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- No usage history -->
            <div v-else-if="!loading" class="usage-history-card text-center text-muted py-6">
              <i class="fas fa-inbox fa-2x mb-3 d-block"></i>
              Chưa có lịch sử sử dụng
            </div>

          </div>
        </div>
      </div>
    </b-overlay>

    <b-modal
      id="modal-edit-customer-info"
      title="Sửa thông tin khách hàng"
      centered
      hide-footer
      @hidden="resetEditCustomer"
    >
      <b-overlay :show="savingCustomer">
        <form @submit.prevent="submitEditCustomer">
          <div class="form-group">
            <label class="font-weight-bold">Họ và tên</label>
            <input v-model="editCustomer.fullNameInput" type="text" class="form-control" />
          </div>
          <div class="form-group">
            <label class="font-weight-bold">Email</label>
            <input v-model="editCustomer.email" type="email" class="form-control" />
          </div>
          <div class="form-group">
            <label class="font-weight-bold">Số điện thoại</label>
            <input v-model="editCustomer.phoneMobile" type="text" maxlength="15" class="form-control" />
            <small class="text-muted">
              Nếu SĐT không đúng định dạng (VD: dữ liệu Zalo cũ bị lưu nhầm), hệ thống sẽ lưu nguyên
              văn thay vì tự thêm "+84". Nhập đúng SĐT thật để hệ thống chuẩn hóa lại.
            </small>
          </div>
          <div class="form-group">
            <label class="font-weight-bold">Giới tính</label>
            <select v-model="editCustomer.gender" class="form-control">
              <option value="">-- Không rõ --</option>
              <option value="Nam">Nam</option>
              <option value="Nữ">Nữ</option>
            </select>
          </div>

          <div class="d-flex justify-content-end mt-4">
            <button type="button" class="btn btn-light mr-2" @click="$bvModal.hide('modal-edit-customer-info')">
              Hủy
            </button>
            <button type="submit" class="btn btn-success" :disabled="savingCustomer">
              <i class="fas fa-save mr-1"></i>Lưu
            </button>
          </div>
        </form>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'PackageRegistrationDetail',
  props: {
    id: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      detail: null,
      savingCustomer: false,
      editCustomer: {
        fullNameInput: '',
        email: '',
        phoneMobile: '',
        gender: '',
      },
    };
  },

  computed: {
    customerName() {
      const acct = this.detail?.accountHis;
      if (!acct) return this.detail?.accountId || '—';
      return acct.fullName || acct.firstName || acct.lastName || '—';
    },
    customerEmail() {
      return this.detail?.accountHis?.email || '—';
    },
    customerPhone() {
      const acct = this.detail?.accountHis;
      if (!acct) return '—';
      return acct.phoneMobile || acct.phoneHome || '—';
    },
    customerDob() {
      const acct = this.detail?.accountHis;
      const dob = acct?.dateOfBirth;
      if (!dob) return '—';
      if (typeof dob === 'number' && dob < 0) return '—';
      if (typeof dob === 'string' && dob.startsWith('0001-')) return '—';
      return this.formatDate(dob);
    },
    customerGender() {
      const g = this.detail?.accountHis?.gender;
      if (!g) return '—';
      if (g === 'M' || g === 'male') return 'Nam';
      if (g === 'F' || g === 'female') return 'Nữ';
      return g;
    },
    packageName() {
      return this.detail?.tranPackage?.name || 'Không rõ gói';
    },
    isActive() {
      if (!this.detail) return false;
      const now = Date.now() / 1000;
      return this.detail.status === 1 && this.detail.endDate > now;
    },
    statusLabel() {
      return this.isActive ? 'Hoạt động' : 'Hết hạn';
    },
    statusBadgeClass() {
      return this.isActive ? 'pill-active' : 'pill-expired';
    },

    // Build a lookup for usage by itemId
    usageByItemId() {
      const map = {};
      const usages = this.detail?.serviceUsage || [];
      usages.forEach((u) => {
        if (!map[u.itemId]) {
          map[u.itemId] = 0;
        }
        map[u.itemId] += u.quantity || 0;
      });
      return map;
    },
  },

  created() {
    this.loadDetail();
  },

  methods: {
    async loadDetail() {
      if (!this.id) return;
      this.loading = true;
      try {
        const res = await this.$api.get(`UserBundleOrders/detail?id=${this.id}`);
        const raw = res?.data ?? res;
        this.detail = raw?.data ?? raw;
      } catch (e) {
        console.error('Lỗi tải chi tiết đăng ký:', e);
        this.$toastr?.e?.({
          title: 'Lỗi',
          msg: 'Không thể tải thông tin chi tiết. Vui lòng thử lại.',
        });
        this.goBack();
      } finally {
        this.loading = false;
      }
    },

    openEditCustomer() {
      const acct = this.detail?.accountHis;
      if (!acct) return;
      this.editCustomer = {
        fullNameInput: acct.fullName || [acct.firstName, acct.middleName, acct.lastName].filter(Boolean).join(' '),
        email: acct.email || '',
        phoneMobile: acct.phoneMobile || '',
        gender: acct.gender || '',
      };
      this.$bvModal.show('modal-edit-customer-info');
    },

    resetEditCustomer() {
      this.editCustomer = { fullNameInput: '', email: '', phoneMobile: '', gender: '' };
    },

    splitFullName(fullName) {
      const parts = (fullName || '').trim().split(/\s+/);
      if (parts.length === 0 || (parts.length === 1 && parts[0] === '')) {
        return { firstName: '', middleName: '', lastName: '' };
      }
      if (parts.length === 1) {
        return { firstName: parts[0], middleName: '', lastName: parts[0] };
      }
      const firstName = parts[0];
      const lastName = parts[parts.length - 1];
      const middleName = parts.slice(1, -1).join(' ');
      return { firstName, middleName, lastName };
    },

    async submitEditCustomer() {
      const acct = this.detail?.accountHis;
      if (!acct) return;

      const { firstName, middleName, lastName } = this.splitFullName(this.editCustomer.fullNameInput);
      if (!firstName && !lastName) {
        this.$toastr?.e?.({ title: 'Lỗi', msg: 'Vui lòng nhập họ và tên khách hàng' });
        return;
      }

      this.savingCustomer = true;
      try {
        const fd = new FormData();
        fd.append('id', acct.id);
        fd.append('firstName', firstName);
        fd.append('middleName', middleName);
        fd.append('lastName', lastName);
        fd.append('email', this.editCustomer.email || '');
        fd.append('phoneMobile', this.editCustomer.phoneMobile || '');
        fd.append('gender', this.editCustomer.gender || '');

        fd.append('status', acct.status ?? 1);
        fd.append('isDeleted', 0);

        await this.$api.put(`AccountHis/${acct.id}`, fd);
        this.$toastr?.s?.({ title: 'Thành công', msg: 'Đã cập nhật thông tin khách hàng' });
        this.$bvModal.hide('modal-edit-customer-info');
        await this.loadDetail();
      } catch (e) {
        this.$toastr?.e?.({
          title: 'Lỗi',
          msg: 'Không thể cập nhật thông tin khách hàng. Vui lòng thử lại.',
        });
      } finally {
        this.savingCustomer = false;
      }
    },

    getUsageVariant() {
      return 'success';
    },

    getUsageItemName(usage) {
      const itemId = usage.itemId;
      if (usage.itemType === 0) {
        const med = (this.detail?.tranMedicine || []).find((m) => m.id === itemId);
        if (med) return med.shortName || med.name;
      } else if (usage.itemType === 1) {
        // 1 = service package
        const pkg = (this.detail?.tranServicePackage || []).find((p) => p.id === itemId);
        if (pkg) return pkg.name;
      } else if (usage.itemType === 2) {
        // 2 = dịch vụ
        const svc = (this.detail?.tranService || []).find((s) => s.id === itemId);
        if (svc) return svc.name;
      }
      return 'Không rõ';
    },

    getItemTypeLabel(type) {
      switch (type) {
        case 0: return 'Thuốc & TPCN';
        case 1: return 'Service Package';
        case 2: return 'Dịch vụ';
        default: return 'Khác';
      }
    },

    formatPrice(val) {
      if (!val && val !== 0) return '0';
      return new Intl.NumberFormat('vi-VN').format(val);
    },

    formatDate(val) {
      if (!val) return '—';
      const ms = typeof val === 'number' && val < 1e12 ? val * 1000 : val;
      return this.$moment(ms).format('D/M/YYYY');
    },

    goBack() {
      this.$router.push({ name: 'package_registration' });
    },

    goToUsageHistory() {
      if (!this.isActive) {
        const expiredDate = this.formatDate(this.detail?.endDate);
        this.$toastr?.e?.({
          title: 'Gói đã hết hạn',
          msg: `Gói dịch vụ "${this.packageName}" đã hết hạn vào ${expiredDate}.`,
        });
        return;
      }
      this.$router.push({
        name: 'usage_recording_create',
        query: { orderId: this.detail?.id || undefined },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.reg-detail-page {
  background-color: #f8f9fa;
  min-height: 100vh;
}

// Info cards (customer + registration)
.info-card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 14px;
  padding: 22px 24px;
  transition: box-shadow 0.25s ease;

  &:hover {
    box-shadow: 0 6px 24px rgba(76, 87, 125, 0.08);
  }

  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 18px;
    gap: 10px;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;

    &--blue {
      background: #eef6ff;
      color: #3b82f6;
    }
    &--green {
      background: #e8fff3;
      color: #50cd89;
    }
  }

  &__title {
    margin-bottom: 0;
    font-weight: 700;
    color: #181c32;
    font-size: 0.95rem;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}

.info-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 0.88rem;

  &__label {
    color: #7e8299;
    min-width: 110px;
    flex-shrink: 0;
  }

  &__value {
    color: #181c32;
    word-break: break-word;
  }
}

// Status pill
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 0.78rem;
  font-weight: 600;
}
.pill-active {
  background: #e8fff3;
  color: #50cd89;
}
.pill-expired {
  background: #fff5f8;
  color: #f1416c;
}

// Remaining cards
.remaining-card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 14px;
  padding: 22px 24px;
  transition: box-shadow 0.25s ease;

  &:hover {
    box-shadow: 0 6px 24px rgba(76, 87, 125, 0.08);
  }

  &__title {
    font-weight: 700;
    color: #181c32;
    font-size: 0.92rem;
    margin-bottom: 18px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f3f4f8;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.remaining-item {
  &__name {
    font-weight: 600;
    font-size: 0.88rem;
    color: #181c32;
  }

  &__count {
    font-weight: 600;
    font-size: 0.85rem;
    color: #7e8299;
  }

  &__bar {
    border-radius: 4px;
  }
}

.text-purple {
  color: #7239ea !important;
}

// Usage history
.usage-history-card {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 14px;
  padding: 22px 24px;

  &__title {
    font-weight: 700;
    color: #181c32;
    font-size: 0.95rem;
    margin-bottom: 18px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f3f4f8;
  }

  &__body {
    display: flex;
    flex-direction: column;
  }
}

.usage-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f8;

  &:last-child {
    border-bottom: none;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__name {
    font-size: 0.92rem;
    color: #181c32;
  }

  &__meta {
    font-size: 0.82rem;
  }

  &__date {
    font-size: 0.85rem;
    white-space: nowrap;
    margin-left: 16px;
    color: #50cd89;
    font-weight: 500;
  }
}
</style>
